// ============================================================
// TMR Portal Admin — os vínculos de uma pessoa com as Empresas (10 B5b)
//
// This is where a `PartyCompanyLink` is actually written. The V1 backfill filled the column once, and the
// Empresa cadastre resolves a person **without** attaching them — the subscriber that would keep the
// reverse index is still open (STATUS, sequela da B8.4). Until it exists, a person only becomes visible to
// a scoped operator because somebody linked them here.
// ============================================================
import { useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { Avatar, Btn, IconTile } from '../../components/primitives';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { RecordPicker } from '../../components/RecordPicker';
import { SelectField } from '../../components/fields';
import { Icons, StrokeIcon } from '../../lib/icons';
import { maskCNPJ } from '../../lib/format';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useCompany, useCompanySearch } from '../../data/companies/queries';
import type { CompanyListItem } from '../../data/companies/contract';
import { useParty, useUpdateParty } from '../../data/parties/queries';
import { PARTY_ROLES, partyRoleLabel, withCompanies } from '../../data/parties/form';
import type { PartyCompanyLink, PartyRecord, PartyRole } from '../../data/parties/contract';
import { OverlayPortal } from '../../components/OverlayPortal';

/** Props for {@link PartyAssociationsModal}. */
export interface PartyAssociationsModalProps {
  partyId: string;
  /** The name the list row already showed, so the header does not wait for the record. */
  partyName: string;
  /** Whether the operator may change the links (`parties.manage`); read-only otherwise. */
  canManage: boolean;
  onClose: () => void;
  onOpenCompany: (companyId: string) => void;
}

/**
 * Shows and edits which Empresas a person answers for, and in what position.
 *
 * The links are edited as a draft and committed in one `PUT /parties/{id}` (`withCompanies`), which
 * replaces the whole set — so the request always carries the person's details untouched, and the operator
 * can undo a mistaken link before it reaches the server.
 */
export function PartyAssociationsModal({
  partyId,
  partyName,
  canManage,
  onClose,
  onOpenCompany,
}: PartyAssociationsModalProps) {
  const record = useParty(partyId);

  return (
    <Shell name={partyName} onClose={onClose}>
      {record.isPending ? (
        <div style={{ padding: 16 }}>
          <Skeleton rows={3} />
        </div>
      ) : record.isError ? (
        <ErrorState error={record.error} onRetry={() => void record.refetch()} />
      ) : (
        <Associations
          record={record.data}
          canManage={canManage}
          onClose={onClose}
          onOpenCompany={onOpenCompany}
        />
      )}
    </Shell>
  );
}

/** The editor itself, mounted only once the whole record is in hand. */
function Associations({
  record,
  canManage,
  onClose,
  onOpenCompany,
}: {
  record: PartyRecord;
  canManage: boolean;
  onClose: () => void;
  onOpenCompany: (companyId: string) => void;
}) {
  const canReadCompany = usePermission(PERMISSIONS.companyRead);
  const [links, setLinks] = useState<PartyCompanyLink[]>(record.companies);
  const [role, setRole] = useState<PartyRole>('Representative');
  const [picking, setPicking] = useState(false);
  const [duplicate, setDuplicate] = useState(false);

  const updateParty = useUpdateParty();
  const dirty = !sameLinks(record.companies, links);

  const add = (company: CompanyListItem) => {
    if (links.some((l) => l.companyId === company.id && l.role === role)) {
      // The backend collapses duplicate (company, role) pairs, so this would look like a save that did
      // nothing. Saying it is the same person in the same position is more useful than a silent no-op.
      setDuplicate(true);
      return;
    }
    setDuplicate(false);
    setLinks((previous) => [...previous, { companyId: company.id, role }]);
  };

  const remove = (link: PartyCompanyLink) => {
    setDuplicate(false);
    setLinks((previous) => previous.filter((l) => !(l.companyId === link.companyId && l.role === link.role)));
  };

  const save = () => {
    updateParty.mutate({ id: record.id, ...withCompanies(record, links) }, { onSuccess: onClose });
  };

  return (
    <>
      <div style={{ flex: 1, overflowY: 'auto', padding: links.length ? '8px 16px' : 0 }}>
        {links.length ? (
          links.map((link) => (
            <LinkRow
              key={`${link.companyId}-${link.role}`}
              link={link}
              canReadCompany={canReadCompany}
              canManage={canManage}
              onOpen={() => onOpenCompany(link.companyId)}
              onRemove={() => remove(link)}
            />
          ))
        ) : (
          <EmptyState
            message="Esta pessoa não está vinculada a nenhuma empresa."
            hint={
              canManage
                ? 'Sem vínculo, ela não aparece para quem tem acesso restrito a uma empresa.'
                : undefined
            }
          />
        )}
      </div>

      {canManage && (
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border-default)', display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ width: 200 }}>
            <SelectField
              label="Vincular como"
              value={role}
              onChange={(v) => setRole(v as PartyRole)}
              options={PARTY_ROLES.map((r) => ({ value: r, label: partyRoleLabel(r) }))}
            />
          </div>
          <Btn variant="secondary" icon={Icons.plus} onClick={() => setPicking(true)}>
            Escolher empresa
          </Btn>
        </div>
      )}

      {(duplicate || updateParty.isError) && (
        <div role="alert" style={{ padding: '10px 24px', fontSize: 12.5, color: 'var(--danger)' }}>
          {duplicate
            ? 'Esta pessoa já ocupa essa posição nessa empresa.'
            : 'Não foi possível salvar os vínculos. Tente de novo.'}
        </div>
      )}

      <div style={{ padding: '12px 24px', borderTop: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
          {dirty ? 'Alterações ainda não salvas.' : `${links.length} ${links.length === 1 ? 'vínculo' : 'vínculos'}`}
        </span>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="secondary" onClick={onClose} disabled={updateParty.isPending}>
            {dirty ? 'Descartar' : 'Fechar'}
          </Btn>
          {canManage && (
            <Btn variant="primary" icon={Icons.check} onClick={save} disabled={!dirty || updateParty.isPending}>
              {updateParty.isPending ? 'Salvando…' : 'Salvar vínculos'}
            </Btn>
          )}
        </div>
      </div>

      {picking && (
        <RecordPicker<CompanyListItem>
          eyebrow="Vínculo"
          title="Escolher empresa"
          subtitle={`A pessoa passa a constar como ${partyRoleLabel(role).toLowerCase()} da empresa escolhida.`}
          placeholder="Buscar por nome, CNPJ ou domínio…"
          useSearch={useCompanySearch}
          getKey={(company) => company.id}
          toRow={(company) => ({
            primary: company.name,
            secondary: company.cnpj ? maskCNPJ(company.cnpj) : 'Sem CNPJ',
          })}
          rowIcon={Icons.clinic}
          idleMessage="Busque a empresa por nome, CNPJ ou domínio."
          notFoundMessage="Nenhuma empresa encontrada."
          onSelect={add}
          onClose={() => setPicking(false)}
        />
      )}
    </>
  );
}

/**
 * One link. The Empresa's name is resolved per row and cached under `companyKeys.detail`, the same way the
 * Contratos list does it — Identity does not join into the company schema (01 §3), so the record carries
 * an id and nothing else. Without `company.read` the row shows the id: holding `parties.read` without
 * `company.read` is a legitimate combination, and inventing a name would be worse than admitting it.
 */
function LinkRow({
  link,
  canReadCompany,
  canManage,
  onOpen,
  onRemove,
}: {
  link: PartyCompanyLink;
  canReadCompany: boolean;
  canManage: boolean;
  onOpen: () => void;
  onRemove: () => void;
}) {
  const company = useCompany(canReadCompany ? link.companyId : undefined);

  const name = !canReadCompany ? (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{link.companyId.slice(0, 8)}</span>
  ) : company.data ? (
    company.data.name
  ) : company.isError ? (
    <span style={{ color: 'var(--fg-subtle)' }}>Empresa fora do seu escopo</span>
  ) : (
    <span style={{ color: 'var(--fg-subtle)' }}>Carregando empresa…</span>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 8px', borderBottom: '1px solid var(--border-default)' }}>
      <IconTile size={34}>
        <StrokeIcon d={Icons.clinic} size={16} />
      </IconTile>
      <button
        type="button"
        onClick={onOpen}
        style={{ flex: 1, minWidth: 0, display: 'block', textAlign: 'left', border: 0, background: 'transparent', cursor: 'pointer', padding: 0 }}
      >
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink-900)' }}>{name}</div>
        <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>{partyRoleLabel(link.role)}</div>
      </button>
      {canManage && (
        <button type="button" onClick={onRemove} className="pa-icon-btn" style={{ width: 30, height: 30 }} title="Remover vínculo">
          <StrokeIcon d={Icons.x} size={14} />
        </button>
      )}
    </div>
  );
}

/** The modal frame, shared by the editor and by the states that stand in for it. */
function Shell({ name, children, onClose }: { name: string; children: ReactNode; onClose: () => void }) {
  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <OverlayPortal>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 85, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div onClick={stop} style={{ width: 560, maxWidth: '100%', maxHeight: '88vh', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <Avatar name={name} size={40} />
            <div style={{ minWidth: 0 }}>
              <div className="pa-eyebrow" style={{ marginBottom: 3 }}>Vínculos com empresas</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 17, margin: 0, letterSpacing: '-0.01em' }}>{name}</h3>
            </div>
          </div>
          {children}
        </div>
      </div>
    </OverlayPortal>
  );
}

/** Whether the draft still matches what was read — set comparison, since order carries no meaning. */
function sameLinks(a: PartyCompanyLink[], b: PartyCompanyLink[]): boolean {
  if (a.length !== b.length) return false;
  const key = (link: PartyCompanyLink) => `${link.companyId}:${link.role}`;
  const left = a.map(key).sort();
  const right = b.map(key).sort();
  return left.every((value, index) => value === right[index]);
}
